import { useCallback, useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * SchoolNavTour — a guided, element-highlighting product tour (driver.js) for
 * school owners. Walks through every page where the school enters data and views
 * reports by spotlighting each sidebar nav item with a bilingual explanation.
 *
 * Triggers:
 *  - Auto on a school_admin's FIRST login (once, localStorage flag).
 *  - Anytime via the window event 'school-tour:start' (Header "Replay Tour" button).
 *
 * Before highlighting, it asks the Sidebar to expand all groups
 * ('school-tour:expand-nav'); steps whose target isn't visible are skipped, so it
 * stays robust across roles/screen sizes.
 */

// Ordered tour: { navId } points at [data-testid="nav-<navId>"]; no navId = centered card.
const STEP_DEFS = [
  { title_hi: 'शिक्षा मित्र में आपका स्वागत है! 🙏', title_en: 'Welcome to Shiksha Mitra! 🙏',
    desc_hi: 'चलिए 2 मिनट में आपको पूरा स्कूल management दिखाते हैं — कहाँ data भरना है और कहाँ reports देखनी हैं। "आगे" दबाते रहिए।',
    desc_en: 'Let us show you the whole school in 2 minutes — where to enter data and where to see reports. Keep pressing "Next".' },
  { navId: 'dashboard', title_hi: '📊 Dashboard', title_en: '📊 Dashboard',
    desc_hi: 'आपके स्कूल की पूरी झलक — students, fees, attendance के live आँकड़े यहाँ दिखते हैं।',
    desc_en: 'A live snapshot of your school — students, fees and attendance numbers at a glance.' },
  { navId: 'setup-guide', title_hi: '🎯 Setup Guide', title_en: '🎯 Setup Guide',
    desc_hi: 'नए स्कूल यहाँ से शुरू करें — step-by-step अपना स्कूल set up करने की checklist।',
    desc_en: 'New schools start here — a step-by-step checklist to set up your school.' },
  { navId: 'students', title_hi: '👨‍🎓 Students', title_en: '👨‍🎓 Students',
    desc_hi: 'यहाँ अपने छात्र जोड़ें, edit करें और manage करें — एक-एक या Bulk Import से।',
    desc_en: 'Add, edit and manage your students here — one by one or via Bulk Import.' },
  { navId: 'teachers', title_hi: '👨‍🏫 Teachers', title_en: '👨‍🏫 Teachers',
    desc_hi: 'अपने शिक्षक और staff यहाँ जोड़ें व manage करें।',
    desc_en: 'Add and manage your teachers and staff here.' },
  { navId: 'classes', title_hi: '📚 Classes', title_en: '📚 Classes',
    desc_hi: 'कक्षाएं और sections यहाँ बनाएं — students इन्हीं से जुड़ते हैं।',
    desc_en: 'Create classes and sections here — students are mapped to these.' },
  { navId: 'attendance', title_hi: '✅ Attendance', title_en: '✅ Attendance',
    desc_hi: 'रोज़ की हाज़िरी यहाँ लगाएं — और trends report में देखें।',
    desc_en: 'Mark daily attendance here — and view trends in reports.' },
  { navId: 'exams', title_hi: '📝 Exams & Results', title_en: '📝 Exams & Results',
    desc_hi: 'परीक्षाएं बनाएं, marks भरें और report cards auto-generate करें।',
    desc_en: 'Create exams, enter marks and auto-generate report cards.' },
  { navId: 'fees', title_hi: '💰 Fees', title_en: '💰 Fees',
    desc_hi: 'फीस collect करें, receipts बनाएं और pending fees track करें।',
    desc_en: 'Collect fees, generate receipts and track pending dues.' },
  { navId: 'fee-structure', title_hi: '📋 Fee Structure', title_en: '📋 Fee Structure',
    desc_hi: 'कक्षा-वार फीस structure यहाँ set करें।',
    desc_en: 'Set your class-wise fee structure here.' },
  { navId: 'timetable', title_hi: '🗓️ Timetable', title_en: '🗓️ Timetable',
    desc_hi: 'कक्षाओं की समय-सारणी यहाँ बनाएं।',
    desc_en: 'Build your class timetable here.' },
  { navId: 'transport', title_hi: '🚌 Transport', title_en: '🚌 Transport',
    desc_hi: 'बस routes, vehicles और students की transport details manage करें।',
    desc_en: 'Manage bus routes, vehicles and student transport details.' },
  { navId: 'library', title_hi: '📖 Library', title_en: '📖 Library',
    desc_hi: 'पुस्तकालय की किताबें और issue/return यहाँ manage करें।',
    desc_en: 'Manage library books and issue/return here.' },
  { navId: 'communication', title_hi: '📢 Announcements', title_en: '📢 Announcements',
    desc_hi: 'अभिभावकों/शिक्षकों को घोषणाएं और WhatsApp messages यहाँ से भेजें।',
    desc_en: 'Send announcements and WhatsApp messages to parents/teachers from here.' },
  { navId: 'bulk', title_hi: '📥 Bulk Import', title_en: '📥 Bulk Import',
    desc_hi: 'Excel से एक साथ सैकड़ों students/teachers का real data upload करें।',
    desc_en: 'Upload hundreds of students/teachers at once from Excel.' },
  { navId: 'reports', title_hi: '📄 Reports', title_en: '📄 Reports',
    desc_hi: 'सभी रिपोर्ट यहाँ — attendance, fees, results — download करें।',
    desc_en: 'All your reports here — attendance, fees, results — ready to download.' },
  { navId: 'school-analytics', title_hi: '📈 Analytics', title_en: '📈 Analytics',
    desc_hi: 'स्कूल के growth और performance के insights यहाँ देखें।',
    desc_en: 'See growth and performance insights for your school here.' },
  { navId: 'data-export', title_hi: '📤 Data Export', title_en: '📤 Data Export',
    desc_hi: 'अपना पूरा data कभी भी यहाँ से export कर सकते हैं — आपका data, आपके पास।',
    desc_en: 'Export all your data anytime — your data stays yours.' },
  { navId: 'settings', title_hi: '⚙️ Settings', title_en: '⚙️ Settings',
    desc_hi: 'School profile, branding और preferences यहाँ update करें।',
    desc_en: 'Update school profile, branding and preferences here.' },
  { title_hi: '🚀 Activate करें — पूरा access', title_en: '🚀 Activate — Full Access',
    desc_hi: 'पसंद आया? Software हमेशा FREE — सिर्फ़ one-time + छोटा सालाना maintenance, आपके कुल users (छात्र+शिक्षक+staff) के slab पर। Settings → Activation से payment करके पूरा access पाएं।',
    desc_en: 'Liked it? Software is always FREE — only a one-time + small yearly maintenance based on your total-users slab. Pay from Settings → Activation for full access.' },
];

export default function SchoolNavTour({ user }) {
  const { language } = useLanguage();
  const t = (hi, en) => (language === 'hi' ? hi : en);
  const activeDrv = useRef(null);

  const startTour = useCallback(() => {
    // Never run on top of the demo WelcomeTour / Diksha guided demo modals — their
    // own overlays would collide with driver.js (and vice-versa).
    if (document.querySelector('[data-testid="welcome-tour"]')) return;
    if (document.querySelector('[data-testid="diksha-guided-demo"]')) return;
    if (document.querySelector('[data-testid="demo-activate-modal"]')) return;
    // Ask the sidebar to expand every group so all nav items render.
    window.dispatchEvent(new Event('school-tour:expand-nav'));
    setTimeout(() => {
      const steps = STEP_DEFS.map((s) => {
        const popover = {
          title: t(s.title_hi, s.title_en),
          description: t(s.desc_hi, s.desc_en),
        };
        if (!s.navId) return { popover };
        const sel = `[data-testid="nav-${s.navId}"]`;
        const el = document.querySelector(sel);
        if (!el || el.offsetParent === null) return null; // hidden/absent → skip
        return { element: sel, popover: { ...popover, side: 'right', align: 'start' } };
      }).filter(Boolean);

      if (steps.length === 0) return;
      const drv = driver({
        showProgress: true,
        allowClose: true,
        overlayColor: 'rgba(17, 24, 39, 0.7)',
        nextBtnText: t('आगे →', 'Next →'),
        prevBtnText: t('← पीछे', '← Back'),
        doneBtnText: t('हो गया ✓', 'Done ✓'),
        progressText: t('{{current}} / {{total}}', '{{current}} / {{total}}'),
        steps,
        onDestroyed: () => { activeDrv.current = null; },
      });
      activeDrv.current = drv;
      drv.drive();
    }, 450);
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the demo WelcomeTour or Diksha guided demo opens (incl. replay), tear down
  // any active driver.js nav-tour so its overlay never blocks those modals.
  useEffect(() => {
    const killDrv = () => {
      try { if (activeDrv.current) { activeDrv.current.destroy(); activeDrv.current = null; } } catch { /* ignore */ }
    };
    window.addEventListener('welcome-tour:replay', killDrv);
    window.addEventListener('diksha-demo:start', killDrv);
    return () => {
      window.removeEventListener('welcome-tour:replay', killDrv);
      window.removeEventListener('diksha-demo:start', killDrv);
    };
  }, []);

  // Auto-run once on a school owner's first login.
  useEffect(() => {
    if (!user || user.role !== 'school_admin' || !user.id) return;
    const key = `navTour:done:${user.id}`;
    if (localStorage.getItem(key)) return;
    // Defer while the first-login language prompt is still pending.
    const langPending = !user?.lang_prompt_seen && !localStorage.getItem(`langPromptDone:${user.id}`);
    if (langPending) return;
    const tm = setTimeout(() => {
      // Don't clash with the demo welcome modal / language prompt if open.
      if (document.querySelector('[data-testid="welcome-tour"]')) return;
      if (document.querySelector('[data-testid="lang-prompt-modal"]')) return;
      startTour();
      try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
    }, 2200);
    return () => clearTimeout(tm);
  }, [user, startTour]);

  // Manual replay via window event (Header button).
  useEffect(() => {
    const h = () => startTour();
    window.addEventListener('school-tour:start', h);
    return () => window.removeEventListener('school-tour:start', h);
  }, [startTour]);

  // After the language prompt is dismissed, run the nav tour (unless the demo
  // welcome tour is showing, which takes precedence).
  useEffect(() => {
    const h = () => {
      if (!user || user.role !== 'school_admin' || !user.id) return;
      const key = `navTour:done:${user.id}`;
      if (localStorage.getItem(key)) return;
      setTimeout(() => {
        if (document.querySelector('[data-testid="welcome-tour"]')) return;
        if (document.querySelector('[data-testid="lang-prompt-modal"]')) return;
        startTour();
        try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
      }, 2400);
    };
    window.addEventListener('lang-prompt:done', h);
    return () => window.removeEventListener('lang-prompt:done', h);
  }, [user, startTour]);

  return null;
}
